import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { useFormik, setNestedObjectValues } from 'formik';
import { Stepper, Container, Step, StepLabel, Typography, Card, CardContent, Button } from '@mui/material';
import Grid from '@mui/material/Grid2';

import { PreferredAssessmentDates } from '../../request/steps';
import { GTag } from '../../../ga';
import { BulkRequest, Test, type AddBulkRequestProps } from '../../../models';
import { BulkRequestTest, BulkRequestReview, BulkRequestLocation } from '../steps';
import { useAddBulkRequestMutation, usePayBulkRequestMutation } from '../../../slices/apis/bulkRequestApi';

const steps = [
	{
		name: 'Location',
		schema: BulkRequest.locationSchema,
		defaultValue: BulkRequest.locationSchema.getDefault(),
	},
	{
		name: 'Tests',
		schema: BulkRequest.testsSchema,
		defaultValue: BulkRequest.testsSchema.getDefault(),
	},
	{
		name: 'Preferred Assessment Date',
		schema: BulkRequest.preferredAssessmentDateSchema,
		defaultValue: BulkRequest.preferredAssessmentDateSchema.getDefault(),
	},
	{
		name: 'Review',
		schema: BulkRequest.reviewSchema,
		defaultValue: BulkRequest.reviewSchema.getDefault(),
		resetOnNavBack: true,
	},
];

const bulkRequestPreferredAssessmentDateLabels = [
	'Preferred Assessment Date',
	'Second Preferred Assessment Date',
	'Third Preferred Assessment Date',
];

const BulkRequestNew = () => {
	const [activeStep, setActiveStep] = useState(0);
	const [disableSubmit, setDisableSubmit] = useState(false);

	const { schema } = steps[activeStep];
	const [
		addBulkRequest,
		{
			data: addBulkRequestResponse,
			isSuccess: isAddBulkRequestSuccess,
			isLoading: isAddBulkRequestLoading,
			isError: isAddBulkRequestError,
		},
	] = useAddBulkRequestMutation();
	const [
		payBulkRequest,
		{
			isSuccess: isPayBulkRequestSuccess,
			isLoading: isPayBulkRequestLoading,
			data: payBulkRequestResponse,
			error: payBulkRequestError,
		},
	] = usePayBulkRequestMutation();

	const handleBack = () => {
		if (steps[activeStep].resetOnNavBack) {
			formik.resetForm({
				values: {
					...formik.values,
					...steps[activeStep].defaultValue,
				},
			});
		}

		setActiveStep((currStep) => currStep - 1);
	};
	
	const formik = useFormik<AddBulkRequestProps>({
		initialValues: BulkRequest.addSchema.getDefault(),
		validationSchema: schema,
		onSubmit: async (values) => {
			await addBulkRequest({
				...values,
				tests: Test.removeNilTest(values.tests),
			});

			GTag('event', 'form_submit_new_bulk_request');
		},
	});

	const onNextClick = async () => {
		const validationErrors = await formik.validateForm();
		if (Object.keys(validationErrors).length > 0) {
			formik.setTouched(setNestedObjectValues(validationErrors, true));
		} else {
			setActiveStep((currStep) => currStep + 1);
		}
	};

	useEffect(() => {
		// disable submit in a hope to prevent duplicated requests being submitted
		if (isAddBulkRequestSuccess) {
			setDisableSubmit(true);
		}

		// disable submit when encounter an submission error
		if (isAddBulkRequestError) {
			setDisableSubmit(true);
		}

		if (isAddBulkRequestSuccess && !isPayBulkRequestLoading && !payBulkRequestError) {
			const { id } = addBulkRequestResponse;

			payBulkRequest({
				id,
				amount: formik.values.amount,
			});
		}

		if (isPayBulkRequestSuccess && payBulkRequestResponse) {
			GTag('event', 'payment_success_bulk_request');

			window.location = payBulkRequestResponse.url;
		}
	}, [
		formik,
		isAddBulkRequestSuccess,
		isPayBulkRequestSuccess,
		isPayBulkRequestLoading,
		isAddBulkRequestError,
		addBulkRequestResponse,
		payBulkRequest,
		payBulkRequestResponse,
		payBulkRequestError,
	]);

	const formContent = (step: number) => {
		switch (step) {
			case 0:
				return <BulkRequestLocation formik={formik} />;
			case 1:
				return <BulkRequestTest formik={formik} allowQtyEditing />;
			case 2:
				return (
					<PreferredAssessmentDates
						formik={formik}
						prefAssessmentDateLabels={bulkRequestPreferredAssessmentDateLabels}
					/>
				);
			case 3:
				return <BulkRequestReview formik={formik} />;
			default:
				return <div>404: Not Found</div>;
		}
	};

	return (
		<>
			<Helmet>
				<title> New Bulk Request - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			<Container sx={{ bgcolor: 'rgba(145, 158, 171, 0.12)', paddingY: 3 }} maxWidth="lg">
				<Typography variant="h4" align="center">
					New Bulk Request
				</Typography>
				<form onSubmit={formik.handleSubmit}>
					<Card sx={{ marginTop: 2 }}>
						<CardContent sx={{ p: { xs: 1, md: 5 } }}>
							<Stepper activeStep={activeStep} orientation="horizontal" alternativeLabel>
								{steps.map((step, index) => (
									<Step key={step.name}>
										<StepLabel>{step.name}</StepLabel>
									</Step>
								))}
							</Stepper>
							<Grid container sx={{ my: 3 }}>
								<Grid size={{ md: 12 }}>{formContent(activeStep)}</Grid>
							</Grid>
							<Grid container spacing={4}>
								<Grid>
									<Button disabled={activeStep === 0} onClick={handleBack} type="button" color="primary">
										Back
									</Button>
								</Grid>
								<Grid>
									{activeStep === steps.length - 1 ? (
										<Button
											loading={isAddBulkRequestLoading || isPayBulkRequestLoading}
											variant="outlined"
											color="brand"
											type="submit"
											disabled={disableSubmit}
											// don't remove key or formik will submit the form even before submit button is clicked
											key="submit"
										>
											<span>Submit</span>
										</Button>
									) : (
										// don't remove key or formik will submit the form even before submit button is clicked
										<Button key="next" variant="outlined" color="primary" onClick={onNextClick} type="button">
											Next
										</Button>
									)}
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</form>
			</Container>
		</>
	);
};

export default BulkRequestNew;
